import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { BLACKBODY_CHUNK } from './ember.js';
import { getPaperTexture, preloadPaperTexture } from '../paper-texture.js';

// The lanterns ARE the light, and there is nothing else in the sky to light.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'lantern',
  family: 'object',
  danceability: 0.35,
  energyLevel: 0.30,
  bpmMin: 50,
  bpmMax: 130,
  movement: 0.40,
  tonality: 0.92,
  prefers: { dynamics: [0.30, 1.0], percussiveness: [0.0, 0.50] },
  enabled: true
};

// Lantern: a sky-lantern release at night - Yi Peng in Chiang Mai. They go up
// in waves, drift, each lit from the inside by its own guttering flame, and go
// out one at a time until the sky is full of slow orange points.
//
// NO BEAT ANYWHERE IN THIS FILE. Every other visualizer here is built on the
// grid; this one is built on the PHRASE and on nothing smaller. A lantern does
// not know the tempo, and a flame that pulses on the beat is a lamp with a
// dimmer on it, not a fire. The only thing the music schedules is the release.
//
// EVERY POSITION IS ANALYTIC, IN THE VERTEX SHADER. A lantern's whole
// trajectory - rise with an initial acceleration, wind drift that grows with
// altitude, a slow yaw sway, and a wobble on the climb rate - is a closed-form
// function of (time - birth) and a four-float seed. The CPU writes three
// numbers per lantern ONCE, at release (its birth time and where on the ground
// it left from), and then never touches it again: there is no position array,
// nothing is integrated, and update() does no per-lantern work at all. That is
// also why 400 of them cost what they do.
//
// THE FLAME IS THE WHOLE PIECE. Paper is only interesting because something is
// burning inside it, so the flicker gets a real three-band noise rather than a
// sine - see FLICKER_HZ - and the paper's brightness is derived FROM it rather
// than animated alongside it.

// --- the object --------------------------------------------------------------
// About a metre tall, a little under a metre across, which is the real thing.
// Narrower at the bottom than the top: a sky lantern is a bag held open by a
// hoop at its mouth and it tapers down to the burner.
const R_TOP = 0.42;
const R_BOT = 0.30;
const HEIGHT = 0.98;
const RADIAL_SEG = 12;
const HEIGHT_SEG = 5;
const SIZE_VARIATION = 0.10;     // +-10% per lantern, on both axes

// --- the flame ---------------------------------------------------------------
// Three bands, an order of magnitude apart, with the amplitude falling as the
// frequency rises. One sine does not gutter, it throbs; two beat against each
// other in a way the eye picks out as periodic; three at these ratios never
// visibly repeat. ?lanternflame=0 freezes them, which is the demonstration.
const FLICKER_HZ = [0.4, 3.0, 11.0];
const FLICKER_AMP = [0.34, 0.16, 0.07];
const FLICKER_HIGH = 0.55;       // bands.high scales the whole excursion
// A few lanterns in any release have a bad burner: the flame sinks, catches
// again, or simply goes out early. It is the detail that stops 400 identical
// objects reading as 400 copies of one object.
const BAD_BURNER_FRAC = 0.09;

// --- the paper ---------------------------------------------------------------
// 0.60 toward off-white. Rice paper is very nearly white and it is the FLAME
// that colours it; a palette-coloured lantern reads as a plastic balloon. A
// blue cover gives faintly blue-grey paper, never blue paper.
const PAPER_WHITE = 0.60;
const PAPER_TINT_SAT = 0.30;
// Where on Ember's blackbody curve the light REACHING THE PAPER sits. Low, in
// the orange: the flame's own core is hotter and is drawn separately.
const FIRE_T_PAPER = 0.42;
const FIRE_WHITE = 0.16;     // a little white in the fire, so the paper keeps a hue
const PAPER_BASE = [0.93, 0.90, 0.84];
const GRAIN_AMOUNT = 0.10;
const RIB_COUNT = 6;             // bamboo staves, in silhouette
const RIB_DARK = 0.42;
const HOOP_DARK = 0.34;

// --- the climb ---------------------------------------------------------------
const RISE_MIN = 0.30;
const RISE_MAX = 0.60;
const RISE_TAU = 1.8;            // seconds of acceleration off the ground
const RISE_MID = 0.20;           // bands.mid, +-20%
const WIND_BASE = 0.16;
const WIND_LOW = 0.9;            // bands.low scales the drift
const WIND_SMOOTH_S = 8.0;       // ... on a gust's time scale
const WIND_ALT = 0.055;          // drift grows with altitude
const SWAY_DEG = 4.0;
const SWAY_PERIOD = [3.0, 6.0];

// --- releases ----------------------------------------------------------------
const WAVE_MIN = 40;
const WAVE_MAX = 80;
const WAVE_BIG = 150;            // every fourth phrase
const WAVE_SPREAD_S = 4.0;       // a wave is not simultaneous
// 22, where the reference says 12. The number that matters is not the width of
// the field, it is the ANGLE it covers from where you are standing: at the
// release distance a 12-unit strip subtends 37 degrees against a 68-degree
// frame, so every wave went up through the middle third and the sides stayed
// empty. 22 spans the frame.
const WAVE_ZONE = 22.0;          // metres of ground the release covers, across
const WAVE_DEPTH = 15.0;         // and in depth, which is what gives it scale
const WAVE_DIST = [13.0, 24.0];  // the centre of that patch
const SINGLES_PER_S = 0.55;      // strays between the waves
// KEEP THE RELEASE OUT FROM UNDER THE COVER. The wave's own azimuth is pushed
// away from whatever the camera is looking at, so a wave never climbs straight
// through the artwork - the safe zone would then dim the one thing the frame is
// about, exactly where it is brightest.
const WAVE_AZIMUTH_CLEAR = 0.50; // radians either side of the view direction
const WAVE_AZIMUTH_BAND = 0.85;  // and the band beyond it a wave may use

// --- going out ---------------------------------------------------------------
// 28 to 58 s, where the reference is 40 to 90. The pool is the constraint, not
// taste: a release every phrase at these sizes needs a slot back roughly every
// minute, and a lantern that has not gone out by then is recycled while still
// lit, which is the one thing that must never be visible. At this window almost
// every lantern is seen to go out before it is far enough away to be retired.
const OUT_MIN = 28.0;
const OUT_MAX = 58.0;
const OUT_FADE = 3.0;            // the flame sinks over this, with a few catches
const EARLY_OUT_FRAC = 0.05;     // some die almost on the ground
const SLOT_LIFE = 78.0;          // after this the slot is reusable
const RETIRE_FADE = 8.0;         // and it fades out over the last of it

// --- the sky -----------------------------------------------------------------
const CAM_HEIGHT = 1.6;
const CAM_PITCH = 40 * Math.PI / 180;
const PAN_PERIOD = 200.0;
const PHRASE_TILT = 0.045;       // the head lifts a little as a wave goes up
const TILT_EASE = 0.9;           // per second: the lift takes a couple of seconds
const GLOW_DECAY = 0.45;

// The burner's on-screen size, at 720p, one world unit away. It has to read
// as a hot POINT inside the envelope, not as a lamp filling it.
const FLAME_PX = 210;
const PEAK_FLAME = 0.90;
// THE PAPER MUST NOT BLOOM, and the cap is PER FRAGMENT while the envelope is
// drawn DoubleSide and additive - so a lantern seen side-on writes both its
// near and far wall and the two sum. The cap is therefore set at half of what
// the paper is allowed to reach: 0.34 twice is 0.68, which sits just under the
// composer's bloom threshold of 0.78. Only the flame is meant to bloom.
// 0.24 per fragment, where a single lantern alone could take 0.34. Measured on
// a real release rather than on one object: a wave puts forty envelopes in the
// same part of the sky, they are additive and DoubleSide, and at 0.34 the
// overlaps clipped to flat white over almost 1% of the frame. Paper does not
// clip. What the cap has to survive is not one lantern, it is the pile.
const PEAK_PAPER = 0.75;
const PAPER_GAIN = 1.55;
const PAPER_ALPHA = 0.85;
const DIST_FADE = 0.026;     // per world unit of depth
const SAFE_FLOOR = 0.5;

// MEASURED ON THE PI, at 1280x400 - which is the resolution a Pi frame budget
// means, its two panels being that size and not 1080p. Median frame, forced to
// completion with a readPixels, with the release waves actually firing so the
// pool is full:
//
//   tier          lanterns   visualizer   + composer
//   2 DoubleSide     400       13.2 ms      32.5 ms
//   2 FrontSide      400       12.6 ms      32.4 ms
//   1 DoubleSide     250       10.6 ms      26.0 ms
//   0 FrontSide      120        5.8 ms      17.8 ms
//
// THE OVERDRAW IS NOT THE PROBLEM. The brief expects the cost to be DoubleSide
// translucency on objects that overlap, and measured it is 0.6 ms of 13.2 -
// five percent, and nothing at all on the composed frame. What costs is the
// shared composer: 19 ms of the 32.5 at tier 2, which is the bloom floor this
// codebase has measured before and which no visualizer can do anything about.
// So DoubleSide stays at tier 1, where the brief only asked for it at tier 2.
//
// Tier 2 lands at 31 fps here, so the frame-time governor will settle the Pi on
// tier 1 or 0 - which is what it is for. Going from additive paper to normal
// blending with depth writing cost 1.7 ms of the 13.2: depth writes are not
// free, and they are what stops the crowd from blowing out.
//
// The first run of this bench reported 122 live lanterns of 400 and a third of
// the real load: its profile held phraseIndex constant, so no wave ever fired
// and only the strays went up. A perf bench has to drive the thing that makes
// the work.
const TIER = {
  0: { n: 120, ribs: false, grain: false, mist: false, side: THREE.FrontSide },
  1: { n: 250, ribs: true, grain: false, mist: true, side: THREE.DoubleSide },
  2: { n: 400, ribs: true, grain: true, mist: true, side: THREE.DoubleSide }
};
const DEFAULT_TIER = 2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// =============================================================================
// The trajectory, shared verbatim by the paper shader and the flame shader so
// the flame is always exactly inside its own lantern. Everything here is a
// function of (age, seed) and uniforms - there is no state.
// =============================================================================
const TRAJECTORY_CHUNK = `
  float h11(float p) {
    p = fract(p * 0.1031);
    p *= p + 33.33;
    p *= p + p;
    return fract(p);
  }
  // Value noise in one dimension: the flicker has to be irregular, and a sine
  // is not.
  float n11(float x) {
    float i = floor(x), f = fract(x);
    f = f * f * (3.0 - 2.0 * f);
    return mix(h11(i), h11(i + 1.0), f);
  }

  // The three-band gutter, normalised to roughly 0..1 around a mean.
  float flicker(vec4 seed, float t, float amp) {
    float a = n11(t * ${FLICKER_HZ[0].toFixed(2)} + seed.x * 71.0) - 0.5;
    float b = n11(t * ${FLICKER_HZ[1].toFixed(2)} + seed.y * 131.0) - 0.5;
    float c = n11(t * ${FLICKER_HZ[2].toFixed(2)} + seed.z * 197.0) - 0.5;
    float f = a * ${FLICKER_AMP[0].toFixed(2)}
            + b * ${FLICKER_AMP[1].toFixed(2)}
            + c * ${FLICKER_AMP[2].toFixed(2)};
    return clamp(0.80 + f * 2.0 * amp, 0.10, 1.35);
  }

  // Height above the release point. Constant climb with a soft start - the
  // envelope fills before it lifts - plus a slow wobble, because hot air is
  // not a lift.
  // THE MUSIC SETS THE RATE, NEVER THE DISTANCE. The clock is the climb clock
  // integrated on the CPU (the integral of the rate since this lantern's
  // release), so a louder mid band makes every lantern climb FASTER from
  // where it is. Multiplying the whole climb by the rate - what this did
  // first - moved a lantern 30 units up by +-6 units within a second each
  // time the band moved: the sky went down and up again.
  float riseAt(vec4 seed, float age, float clock) {
    float v = mix(${RISE_MIN.toFixed(2)}, ${RISE_MAX.toFixed(2)}, seed.x);
    float e = age - ${RISE_TAU.toFixed(2)} * (1.0 - exp(-age / ${RISE_TAU.toFixed(2)}));
    float wob = (n11(age * 0.23 + seed.w * 53.0) - 0.5) * 0.55;
    // e/age is the soft start's share of the distance, 0 at release and 1 later
    return v * clock * (e / max(age, 0.001)) + wob;
  }

  // Wind. One slow field sampled at the lantern's own position, so everything
  // in the same part of the sky leans the same way without moving as a block -
  // the altitude term alone separates them, because they are never at the
  // same height.
  vec2 windAt(vec2 ground, float y, float t, float gain) {
    float p = t * 0.06;
    vec2 w = vec2(
      n11(ground.x * 0.09 + y * ${WIND_ALT.toFixed(3)} + p) - 0.5,
      n11(ground.y * 0.09 + y * ${WIND_ALT.toFixed(3)} + p + 19.0) - 0.5
    );
    // Drift accumulates: the higher it is, the longer the wind has had it.
    return w * gain * (0.35 + y * 0.09) * 2.0;
  }
`;

// =============================================================================
// Paper
// =============================================================================
const paperVertex = `
  precision mediump float;

  attribute float aBirth;
  attribute float aClockBirth;   // the climb clock at release
  attribute vec2 aGround;
  attribute vec4 aSeed;

  uniform float uTime;
  uniform float uWind;
  uniform float uClock;         // the climb clock now
  uniform float uHighAmp;
  uniform float uFlameFrozen;
  uniform float uHold;        // 1 = the close-up: no climb, no drift
  uniform float uFocalPx;     // focal length in pixels, for the edge fade

  varying vec2 vUv;
  varying float vFlame;       // the burner's current output, 0..1.35
  varying float vAlive;       // 0 once the slot has retired
  varying float vSeed;
  varying float vFold;
  varying vec4 vClip;
  varying float vDepth;
  varying float vLit;        // the extinction envelope, without the flicker
  varying float vFacing;     // 0 exactly on the silhouette
  varying float vEdge;       // how much facing one screen pixel is worth there
  varying float vRimW;       // and the same for the open rims, in uv

  ${TRAJECTORY_CHUNK}

  void main() {
    float age = uTime - aBirth;
    // A dormant or retired slot is collapsed to a point behind the camera
    // rather than branched around: no discard, no overdraw, one multiply.
    float live = step(0.0, age) * step(age, ${SLOT_LIFE.toFixed(1)});

    float y = mix(riseAt(aSeed, max(age, 0.0), max(uClock - aClockBirth, 0.0)), 1.05, uHold);
    vec2 drift = windAt(aGround, y, uTime, uWind) * (1.0 - uHold);
    vec3 centre = vec3(aGround.x + drift.x, y, aGround.y + drift.y);

    // The sway: hung from its own hot column, a lantern turns slowly about its
    // axis and leans a few degrees with it.
    float per = mix(${SWAY_PERIOD[0].toFixed(1)}, ${SWAY_PERIOD[1].toFixed(1)}, aSeed.y);
    float sw = sin(6.2831853 * age / per + aSeed.z * 6.2831853);
    float lean = sw * ${SWAY_DEG.toFixed(1)} * 0.01745;

    // Per-lantern proportions.
    float sx = 1.0 + (aSeed.x - 0.5) * 2.0 * ${SIZE_VARIATION.toFixed(2)};
    float sy = 1.0 + (aSeed.w - 0.5) * 2.0 * ${SIZE_VARIATION.toFixed(2)};

    vec3 p = position;
    // The mouth of the bag is torn and uneven - the bottom ring only.
    float bottom = 1.0 - step(0.02, uv.y);
    p.y += bottom * (n11(uv.x * 37.0 + aSeed.y * 23.0) - 0.5) * 0.10;
    p.x *= sx; p.z *= sx; p.y *= sy;

    // lean about x, then yaw about y
    float cl = cos(lean), sl = sin(lean);
    p.yz = vec2(p.y * cl - p.z * sl, p.y * sl + p.z * cl);
    float ya = sw * 0.6 + aSeed.x * 6.2831853;
    float cy = cos(ya), syw = sin(ya);
    p.xz = vec2(p.x * cy - p.z * syw, p.x * syw + p.z * cy);

    // The normal rides the same two rotations as the position.
    vec3 nrm = normal;
    nrm.yz = vec2(nrm.y * cl - nrm.z * sl, nrm.y * sl + nrm.z * cl);
    nrm.xz = vec2(nrm.x * cy - nrm.z * syw, nrm.x * syw + nrm.z * cy);

    vec3 world = centre + p;

    // --- the burner ---
    float ft = mix(uTime, 0.0, uFlameFrozen);
    float out0 = mix(${OUT_MIN.toFixed(1)}, ${OUT_MAX.toFixed(1)}, aSeed.z);
    // a few burners fail almost immediately
    out0 = mix(out0, 2.0 + aSeed.y * 7.0, step(aSeed.w, ${EARLY_OUT_FRAC.toFixed(2)}));
    float dying = clamp((age - out0) / ${OUT_FADE.toFixed(1)}, 0.0, 1.0);
    // it does not fade smoothly - it sinks, catches twice, and is gone
    float catches = 1.0 - dying + 0.30 * (1.0 - dying) * n11(age * 5.5 + aSeed.x * 11.0);
    float lit = clamp(catches, 0.0, 1.0);
    float bad = step(aSeed.y, ${BAD_BURNER_FRAC.toFixed(2)});
    lit *= mix(1.0, 0.45 + 0.55 * n11(age * 0.6 + aSeed.z * 31.0), bad);
    vFlame = flicker(aSeed, ft, mix(uHighAmp, 0.0, uFlameFrozen)) * lit;
    vLit = lit;

    float retire = 1.0 - clamp((age - (${SLOT_LIFE.toFixed(1)} - ${RETIRE_FADE.toFixed(1)}))
                               / ${RETIRE_FADE.toFixed(1)}, 0.0, 1.0);
    vAlive = live * retire;

    vUv = uv;
    vSeed = aSeed.x;
    // Creases in the envelope, which make the transmission uneven.
    vFold = n11(uv.x * 9.0 + aSeed.w * 17.0) * 0.5 + n11(uv.y * 5.0 + aSeed.z * 13.0) * 0.5;

    vec4 mv = modelViewMatrix * vec4(world, 1.0);
    // Collapse a dead slot rather than branching.
    mv.xyz *= live;
    mv.z -= (1.0 - live) * 1e4;
    vClip = projectionMatrix * mv;
    gl_Position = vClip;

    // ONE PIXEL OF SILHOUETTE, worked out rather than sampled. For a cylinder
    // of screen radius Rpx, a point whose normal is 'facing' away from
    // perpendicular sits about Rpx * facing^2 / 2 pixels inside the outline -
    // so one pixel of edge is facing = sqrt(2 / Rpx). It gets wider as the
    // lantern gets smaller, which is exactly when the staircase shows.
    float depth = max(-mv.z, 0.05);
    vDepth = depth;
    vec3 nView = normalize(mat3(modelViewMatrix) * nrm);
    vFacing = abs(nView.z);
    float rPx = max(uFocalPx * ${R_TOP.toFixed(2)} * sx / depth, 1.0);
    vEdge = clamp(sqrt(2.0 / rPx), 0.02, 0.9);
    // The rims are a fade in uv, so their pixel width comes from the on-screen
    // HEIGHT instead.
    float hPx = max(uFocalPx * ${HEIGHT.toFixed(2)} * sy / depth, 1.0);
    vRimW = clamp(1.0 / hPx, 0.002, 0.25);
  }
`;

const paperFragment = `
  precision mediump float;

  uniform vec3 uPaper;
  uniform float uPaperGain;
  uniform sampler2D uGrain;
  uniform float uHasGrain;
  uniform float uRibs;
  uniform float uZoneDebug;

  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  varying vec2 vUv;
  varying float vFlame;
  varying float vAlive;
  varying float vSeed;
  varying float vFold;
  varying vec4 vClip;
  varying float vDepth;
  varying float vLit;
  varying float vFacing;
  varying float vEdge;
  varying float vRimW;

  ${BLACKBODY_CHUNK}

  void main() {
    if (vAlive <= 0.001) discard;

    // TRANSMISSION FALLS WITH DISTANCE FROM THE BURNER. The flame sits at the
    // mouth, so the bottom of the envelope is bright and the crown darker -
    // that gradient is most of what says "lit from inside" rather than
    // "painted".
    //
    // WITH A FLOOR, AND A GENTLE EXPONENT. A pure exp(-2.35y) is the honest
    // inverse-square answer for a point source and it is wrong for this: it
    // leaves the crown at 9% and the lantern reads as a lit ring with a dark
    // bag over it. The envelope is a diffuser - light bounces around inside
    // it - and it also catches the glow of every other lantern in the sky, so
    // the crown sits at about a third of the mouth, not a tenth.
    float up = vUv.y;
    float reach = 0.30 + 0.70 * exp(-up * 1.35);

    // THE FIRE'S COLOUR ON THE PAPER IS NOT THE FIRE'S COLOUR IN THE AIR.
    // Driving blackbody() at the same temperature for both leaves the blue
    // channel at 0.47 and the envelope comes out olive - the paper stops
    // reading as paper. Rice paper lit from behind by a candle is warm WHITE
    // with a yellow cast, so the paper is shaded from a hotter point on the
    // same curve and mixed in at half strength.
    // THE PAPER IS MULTIPLIED BY THE FLAME'S COLOUR. Not mixed toward it -
    // multiplied, which is what transmission through a sheet actually is, and
    // which is why a lantern is amber whatever the cover happens to be.
    //
    // THE TEMPERATURE HAS TO BE LOW. This was the whole bug: the fire was
    // being evaluated at 0.66-0.86 on Ember's curve, and up there the curve is
    // (1, 1, 0.6-0.9) - which is WHITE. Chasing an olive cast earlier, the
    // range was pushed up until nothing was left of the colour, and 400 white
    // cylinders is what came out. Orange lives between 0.45 and 0.62, where
    // green is still climbing and blue has not started:
    //     blackbody(0.45) = (1.00, 0.42, 0.00)
    //     blackbody(0.55) = (1.00, 0.65, 0.00)
    //     blackbody(0.62) = (1.00, 0.81, 0.04)
    // A little white is mixed INTO the fire, not into the result, so the paper
    // keeps a trace of its own hue instead of having its blue channel zeroed.
    float ft = clamp(${FIRE_T_PAPER.toFixed(2)} + vFlame * 0.14, 0.0, 1.0);
    vec3 fire = mix(blackbody(ft), vec3(1.0), ${FIRE_WHITE.toFixed(2)});
    vec3 body = uPaper * fire;
    float trans = vFlame * reach * (0.72 + 0.56 * vFold);

    // THE BAMBOO, IN SILHOUETTE. Staves up the sides and the hoop at the mouth,
    // darker because they block the light rather than transmit it. This is the
    // detail that sells the paper: without it the envelope is a smooth shell.
    float ribs = 0.0;
    if (uRibs > 0.5) {
      float a = fract(vUv.x * ${RIB_COUNT.toFixed(1)});
      float stave = 1.0 - smoothstep(0.02, 0.075, min(a, 1.0 - a));
      float hoop = 1.0 - smoothstep(0.012, 0.045, abs(vUv.y - 0.085));
      ribs = max(stave * ${RIB_DARK.toFixed(2)}, hoop * ${HOOP_DARK.toFixed(2)});
    }

    float grain = 1.0;
    if (uHasGrain > 0.5) {
      float g = texture2D(uGrain, vUv * vec2(2.0, 1.0) + vec2(vSeed * 7.0, 0.0)).r;
      grain = 1.0 + (g - 0.5) * ${GRAIN_AMOUNT.toFixed(2)};
    }

    // The inside face is brighter than the outside: you are seeing the lit
    // surface directly rather than through a sheet of paper.
    float face = gl_FrontFacing ? 0.78 : 1.0;

    // A LANTERN FURTHER AWAY IS DARKER. Without it the crowd has no depth and
    // the far ones read as near ones that happen to be small.
    float far = 1.0 / (1.0 + vDepth * ${DIST_FADE.toFixed(3)});

    vec3 col = body * trans * (1.0 - ribs) * grain * face * far * uPaperGain;

    vec2 ndc = vClip.xy / max(vClip.w, 0.0001);
    vec2 sp = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(sp - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    col *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));

    // NOT ADDITIVE. This is the other half of the white-out: forty envelopes
    // stacked additively in the same part of the sky sum to flat white however
    // low the per-fragment cap is set. Paper is not a light source you can add
    // up - a lantern in front of another one HIDES it. Normal blending at 0.85
    // with depth writing on, so the near wall always wins and the exposure is
    // bounded by construction rather than by a clamp.
    col = min(col * vAlive, vec3(${PEAK_PAPER.toFixed(2)}));

    // ONE PIXEL OF EDGE. The silhouette of a cylinder is where the normal
    // turns perpendicular to the eye, and at that grazing angle a screen pixel
    // covers a large slice of the surface - which is the staircase on every
    // lantern's outline. vEdge is that slice, worked out analytically from the
    // radius and the depth (see the vertex shader) rather than from fwidth(),
    // which this codebase does not depend on.
    float edge = smoothstep(0.0, vEdge, vFacing);
    // The open rims at top and bottom are geometric edges rather than
    // silhouettes, and need their own fade.
    float rim = smoothstep(0.0, vRimW, vUv.y) * smoothstep(0.0, vRimW, 1.0 - vUv.y);

    // A LANTERN THAT HAS GONE OUT MUST NOT BECOME A BLACK HOLE. Now that the
    // paper is opaque and writes depth, an extinguished envelope painted a
    // near-black rectangle over everything behind it - a hard-edged silhouette,
    // which is the opposite of "keeps rising, dark, and disappears into the
    // sky". Its opacity goes with its flame, so it fades out of the picture
    // instead of punching a hole in it.
    float opacity = ${PAPER_ALPHA.toFixed(2)} * mix(0.06, 1.0, vLit);
    gl_FragColor = vec4(col, opacity * vAlive * edge * rim);
  }
`;

// =============================================================================
// Flames: one additive point per lantern. It is the burner seen through the
// mouth when the lantern is near, and it is the whole lantern when it is far -
// which is what gives the sky its depth. Same trajectory, so it is always in
// the right place.
// =============================================================================
const flameVertex = `
  precision mediump float;

  attribute float aBirth;
  attribute float aClockBirth;   // the climb clock at release
  attribute vec2 aGround;
  attribute vec4 aSeed;

  uniform float uTime;
  uniform float uWind;
  uniform float uClock;         // the climb clock now
  uniform float uHighAmp;
  uniform float uFlameFrozen;
  uniform float uHold;
  uniform float uPixel;

  varying float vFlame;
  varying float vAlive;

  ${TRAJECTORY_CHUNK}

  void main() {
    float age = uTime - aBirth;
    float live = step(0.0, age) * step(age, ${SLOT_LIFE.toFixed(1)});
    float y = mix(riseAt(aSeed, max(age, 0.0), max(uClock - aClockBirth, 0.0)), 1.05, uHold);
    vec2 drift = windAt(aGround, y, uTime, uWind) * (1.0 - uHold);
    // At the mouth, not at the centre: the burner hangs under the envelope.
    vec3 world = vec3(aGround.x + drift.x, y - ${(HEIGHT * 0.34).toFixed(3)},
                      aGround.y + drift.y);

    float ft = mix(uTime, 0.0, uFlameFrozen);
    float out0 = mix(${OUT_MIN.toFixed(1)}, ${OUT_MAX.toFixed(1)}, aSeed.z);
    out0 = mix(out0, 2.0 + aSeed.y * 7.0, step(aSeed.w, ${EARLY_OUT_FRAC.toFixed(2)}));
    float dying = clamp((age - out0) / ${OUT_FADE.toFixed(1)}, 0.0, 1.0);
    float catches = 1.0 - dying + 0.30 * (1.0 - dying) * n11(age * 5.5 + aSeed.x * 11.0);
    float lit = clamp(catches, 0.0, 1.0);
    float bad = step(aSeed.y, ${BAD_BURNER_FRAC.toFixed(2)});
    lit *= mix(1.0, 0.45 + 0.55 * n11(age * 0.6 + aSeed.z * 31.0), bad);
    vFlame = flicker(aSeed, ft, mix(uHighAmp, 0.0, uFlameFrozen)) * lit;

    float retire = 1.0 - clamp((age - (${SLOT_LIFE.toFixed(1)} - ${RETIRE_FADE.toFixed(1)}))
                               / ${RETIRE_FADE.toFixed(1)}, 0.0, 1.0);
    vAlive = live * retire;

    vec4 mv = modelViewMatrix * vec4(world, 1.0);
    mv.xyz *= live;
    mv.z -= (1.0 - live) * 1e4;
    gl_Position = projectionMatrix * mv;

    // The halo's radius follows the flame, and the floor of 1.7 px is what
    // keeps a lantern a kilometre up from vanishing: past a certain distance
    // the envelope is sub-pixel and only the fire is left, as a hot star.
    float depth = max(-mv.z, 0.05);
    gl_PointSize = clamp(uPixel * (0.55 + 0.75 * vFlame) / depth, 1.7, 90.0);
  }
`;

const flameFragment = `
  precision mediump float;
  varying float vFlame;
  varying float vAlive;
  ${BLACKBODY_CHUNK}
  void main() {
    if (vAlive <= 0.001) discard;
    vec2 d = gl_PointCoord - 0.5;
    float r2 = dot(d, d);
    if (r2 > 0.25) discard;
    // A small hot core in a soft halo.
    // YELLOW AT THE CORE, ORANGE AT THE EDGE - which is a temperature
    // gradient across the sprite, not one colour faded out. The core runs hot
    // enough to go near-white; the skirt sits in the same orange the paper is
    // lit by, so the burner and the envelope belong to one fire.
    float core = 1.0 - smoothstep(0.0, 0.030, r2);
    float halo = pow(1.0 - smoothstep(0.0, 0.25, r2), 2.0);
    float t = mix(${FIRE_T_PAPER.toFixed(2)}, 0.86, core) + vFlame * 0.10;
    vec3 fire = blackbody(clamp(t, 0.0, 1.0));
    vec3 col = fire * (halo * 0.70 + core * 0.85) * vFlame * vAlive;
    gl_FragColor = vec4(min(col, vec3(${PEAK_FLAME.toFixed(2)})), 1.0);
  }
`;

// =============================================================================
// Sky. A full-frame quad that reconstructs the view ray, so the stars are
// fixed in the world and the slow pan moves through them - which a screen-space
// gradient would not do, and which is the only thing telling you the camera is
// turning at all once the lanterns are far away.
// =============================================================================
const skyVertex = `
  precision mediump float;
  varying vec2 vNdc;
  void main() {
    vNdc = position.xy * 2.0;
    gl_Position = vec4(position.xy * 2.0, 0.999, 1.0);
  }
`;
const skyFragment = `
  precision mediump float;
  uniform mat4 uInvProj;
  uniform mat4 uCamWorld;
  uniform vec3 uHorizon;
  uniform vec3 uZenith;
  uniform vec3 uGlowCol;
  uniform float uGlow;
  uniform float uMist;
  uniform float uStars;
  varying vec2 vNdc;

  float h21(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }

  void main() {
    vec4 h = uInvProj * vec4(vNdc, -1.0, 1.0);
    vec3 dir = normalize((uCamWorld * vec4(h.xyz / h.w, 0.0)).xyz);

    float up = clamp(dir.y * 0.5 + 0.5, 0.0, 1.0);
    vec3 col = mix(uHorizon, uZenith, smoothstep(0.42, 0.95, up));

    // Fine stars, on a lattice in DIRECTION space so they sit still while the
    // camera pans.
    if (uStars > 0.5) {
      vec2 sp = vec2(atan(dir.z, dir.x) * 8.0, dir.y * 14.0);
      vec2 ci = floor(sp), cf = fract(sp);
      float r = h21(ci);
      if (r > 0.86) {
        vec2 c = vec2(h21(ci + 3.1), h21(ci + 7.7));
        float d = length(cf - c);
        float s = (1.0 - smoothstep(0.0, 0.085, d)) * (0.25 + 0.75 * h21(ci + 11.3));
        col += vec3(0.82, 0.86, 1.0) * s * 0.30 * smoothstep(0.02, 0.35, dir.y);
      }
    }

    // The ground glow: the lanterns still down there, out of frame, lighting
    // the low mist. It breathes with the releases rather than with the beat.
    float low = smoothstep(0.30, -0.12, dir.y);
    col += uGlowCol * low * uGlow;
    if (uMist > 0.5) {
      float band = exp(-abs(dir.y - 0.02) * 16.0);
      col += uGlowCol * band * uGlow * 0.55;
    }

    gl_FragColor = vec4(col, 1.0);
  }
`;

export class LanternVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._forceWave = urlParam('lanternwave') === '1';
    this._flameFrozen = urlParam('lanternflame') === '0';
    this._single = urlParam('lanternsingle') === '1';
    preloadPaperTexture();
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const layout = TIER[this._tier];
    this._layout = layout;
    const N = this._single ? 1 : layout.n;
    this._n = N;

    // --- per-lantern attributes. Written at release, never per frame. ---
    this._birth = new Float32Array(N);
    this._clockBirth = new Float32Array(N);
    this._ground = new Float32Array(N * 2);
    this._seed = new Float32Array(N * 4);
    for (let i = 0; i < N; i++) {
      this._birth[i] = -1e6;
      for (let k = 0; k < 4; k++) this._seed[i * 4 + k] = Math.random();
    }
    this._birthAttr = new THREE.InstancedBufferAttribute(this._birth, 1);
    this._clockBirthAttr = new THREE.InstancedBufferAttribute(this._clockBirth, 1);
    this._clockBirthAttr.setUsage(THREE.DynamicDrawUsage);
    this._groundAttr = new THREE.InstancedBufferAttribute(this._ground, 2);
    this._seedAttr = new THREE.InstancedBufferAttribute(this._seed, 4);
    this._birthAttr.setUsage(THREE.DynamicDrawUsage);
    this._groundAttr.setUsage(THREE.DynamicDrawUsage);

    this._shared = Object.assign({}, safeZoneUniforms, {
      uTime: { value: 0 },
      uWind: { value: WIND_BASE },
      uClock: { value: 0 },
      uHighAmp: { value: 0.6 },
      uFlameFrozen: { value: this._flameFrozen ? 1 : 0 },
      uHold: { value: this._single ? 1 : 0 }
    });
    this._focalScratch = 0;

    // --- the envelope ---
    const cone = new THREE.CylinderGeometry(R_TOP, R_BOT, HEIGHT, RADIAL_SEG, HEIGHT_SEG, true);
    const geo = new THREE.InstancedBufferGeometry();
    geo.index = cone.index;
    geo.attributes.position = cone.attributes.position;
    geo.attributes.uv = cone.attributes.uv;
    geo.attributes.normal = cone.attributes.normal;
    geo.setAttribute('aBirth', this._birthAttr);
    geo.setAttribute('aClockBirth', this._clockBirthAttr);
    geo.setAttribute('aGround', this._groundAttr);
    geo.setAttribute('aSeed', this._seedAttr);
    geo.instanceCount = N;
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    this._cone = cone;
    this._geo = geo;

    this._paperUniforms = Object.assign({}, this._shared, {
      uPaper: { value: new THREE.Color(PAPER_BASE[0], PAPER_BASE[1], PAPER_BASE[2]) },
      uPaperGain: { value: PAPER_GAIN },
      uGrain: { value: null },
      uHasGrain: { value: 0 },
      uRibs: { value: layout.ribs ? 1 : 0 },
      uFocalPx: { value: 520 },
      uZoneDebug: { value: 0 }
    });
    this._paperMat = new THREE.ShaderMaterial({
      uniforms: this._paperUniforms,
      vertexShader: paperVertex,
      fragmentShader: paperFragment,
      side: layout.side,
      transparent: true,
      blending: THREE.NormalBlending,
      // DEPTH WRITING ON, which is what makes DoubleSide behave. Every pixel
      // inside a lantern's outline is covered by both its near wall and its
      // far one, and the depth test resolves that to the near wall for free,
      // in any draw order. Through the open mouth there is only the far wall,
      // so looking up into a lantern still shows its lit inside.
      depthWrite: true,
      depthTest: true
    });
    this._mesh = new THREE.Mesh(geo, this._paperMat);
    this._mesh.frustumCulled = false;
    this._mesh.renderOrder = 10;
    // The edge fade is measured in PIXELS, so the shader needs the real
    // drawing buffer - which only the renderer knows.
    const self = this;
    this._mesh.onBeforeRender = function (renderer) {
      renderer.getDrawingBufferSize(_size);
      const fov = (self._camera.fov || 68) * Math.PI / 360;
      self._paperUniforms.uFocalPx.value = (_size.y * 0.5) / Math.tan(fov);
      self._flameUniforms.uPixel.value = FLAME_PX * (_size.y / 720);
    };
    scene.add(this._mesh);

    // --- the flames ---
    const fgeo = new THREE.BufferGeometry();
    fgeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(N * 3), 3));
    fgeo.setAttribute('aBirth', new THREE.BufferAttribute(this._birth, 1));
    fgeo.setAttribute('aClockBirth', new THREE.BufferAttribute(this._clockBirth, 1));
    fgeo.attributes.aClockBirth.setUsage(THREE.DynamicDrawUsage);
    fgeo.setAttribute('aGround', new THREE.BufferAttribute(this._ground, 2));
    fgeo.setAttribute('aSeed', new THREE.BufferAttribute(this._seed, 4));
    fgeo.attributes.aBirth.setUsage(THREE.DynamicDrawUsage);
    fgeo.attributes.aGround.setUsage(THREE.DynamicDrawUsage);
    fgeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    this._fgeo = fgeo;
    this._flameUniforms = Object.assign({}, this._shared, { uPixel: { value: FLAME_PX } });
    this._flameMat = new THREE.ShaderMaterial({
      uniforms: this._flameUniforms,
      vertexShader: flameVertex,
      fragmentShader: flameFragment,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      // NO DEPTH TEST. The burner sits INSIDE its own envelope, and now that
      // the paper writes depth the paper would hide it - the one thing the
      // whole piece is about. It is drawn after the paper and simply added on
      // top, which is also what a flame seen through rice paper does.
      depthTest: false
    });
    this._flames = new THREE.Points(fgeo, this._flameMat);
    this._flames.frustumCulled = false;
    this._flames.renderOrder = 20;
    scene.add(this._flames);

    // --- the sky ---
    this._skyUniforms = {
      uInvProj: { value: new THREE.Matrix4() },
      uCamWorld: { value: new THREE.Matrix4() },
      uHorizon: { value: new THREE.Color().setRGB(0.020, 0.030, 0.062, THREE.LinearSRGBColorSpace) },
      uZenith: { value: new THREE.Color().setRGB(0.003, 0.005, 0.013, THREE.LinearSRGBColorSpace) },
      uGlowCol: { value: new THREE.Color().setRGB(0.32, 0.16, 0.05, THREE.LinearSRGBColorSpace) },
      uGlow: { value: 0.25 },
      uMist: { value: layout.mist ? 1 : 0 },
      uStars: { value: 1 }
    };
    this._skyMat = new THREE.ShaderMaterial({
      uniforms: this._skyUniforms,
      vertexShader: skyVertex, fragmentShader: skyFragment,
      depthTest: false, depthWrite: false
    });
    this._sky = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._skyMat);
    this._sky.frustumCulled = false;
    this._sky.renderOrder = -1000;
    scene.add(this._sky);

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._slot = 0;
    this._pending = [];          // scheduled releases: [timeToFire, count]
    this._pendingHead = 0;
    this._lastPhrase = null;
    this._phraseCount = 0;
    this._glow = 0.25;
    this._tilt = 0;
    this._tiltKick = 0;
    this._yaw = 0;
    this._clock = 0;             // the climb clock: the integral of the climb rate
    this._windSmooth = 0.5;
    this._midSmooth = 0.5;
    this._highSmooth = 0.5;
    this._singleDone = false;
    this._lastPaletteRef = undefined;
    this._nextSingleAt = 0;
    this._nextWaveAt = 8;
    this._waveAz = 0;
    this._waveDist = 16;
    this._grainSet = false;
    this._scratch = new THREE.Vector3();

    this._placeCamera(0);
    if (this._single) this._seedSingle();
    else this._preSeed();
  }

  // ------------------------------------------------------------- releases --
  // The pool is a ring. A slot comes back round only after SLOT_LIFE, by which
  // time its lantern is out and far away, and it fades over the last eight
  // seconds so the hand-over is never seen.
  _release(az, dist, spreadFrac) {
    const i = this._slot;
    this._slot = (this._slot + 1) % this._n;
    // A PATCH OF GROUND, NOT AN ARC. Giving every lantern in a wave the same
    // distance (give or take a couple of metres) puts the whole release on one
    // curved sheet, and a sheet of identical objects at identical scale reads
    // as a sprite grid - which is exactly what it looked like. Spreading the
    // depth over the full release field is what gives a wave its near ones and
    // its far ones, and therefore its perspective.
    const off = (Math.random() - 0.5) * WAVE_ZONE * spreadFrac;
    const d = dist + (Math.random() - 0.5) * WAVE_DEPTH * spreadFrac;
    const sa = Math.sin(az), ca = Math.cos(az);
    this._ground[i * 2] = sa * d - ca * off;
    this._ground[i * 2 + 1] = ca * d + sa * off;
    this._birth[i] = this._time;
    this._clockBirth[i] = this._clock;
    for (let k = 0; k < 4; k++) this._seed[i * 4 + k] = Math.random();
    this._dirty = true;
  }

  // A wave is spread over four seconds on a rise-and-fall distribution, so it
  // is SEEN to form: a few at first, then the body of it, then stragglers.
  _scheduleWave(count) {
    const az = this._waveAzimuth();
    const dist = lerp(WAVE_DIST[0], WAVE_DIST[1], Math.random());
    this._waveAz = az;
    this._waveDist = dist;
    for (let k = 0; k < count; k++) {
      // Inverse of a raised-cosine: dense in the middle of the window.
      const u = (k + Math.random()) / count;
      const t = WAVE_SPREAD_S * (u - Math.sin(2 * Math.PI * u) / (2 * Math.PI));
      this._pending.push(this._time + t, az, dist);
    }
    this._glow = Math.min(1.0, this._glow + 0.35 + count / 300);
    this._tiltKick = PHRASE_TILT;   // eased into by update(), never a jump
  }

  // OFF TO ONE SIDE OF WHERE THE CAMERA IS LOOKING, never straight ahead and
  // never behind. Straight ahead puts the wave under the artwork, where the
  // safe zone would dim the one thing worth watching. A uniform draw round the
  // whole circle is worse in the other direction: the camera sees a fifth of
  // the sky at a time and turns once in two hundred seconds, so four waves in
  // five went up out of frame and the sky stayed empty. This band is clear of
  // the cover and drifts into view as the pan comes round.
  _waveAzimuth() {
    const side = Math.random() < 0.5 ? -1 : 1;
    const off = WAVE_AZIMUTH_CLEAR + Math.random() * WAVE_AZIMUTH_BAND;
    return this._yaw + side * off;
  }

  // Start with a sky that already has history in it, rather than with an empty
  // one that fills over the first minute.
  _preSeed() {
    const waves = 5;
    for (let w = 0; w < waves; w++) {
      const age = SLOT_LIFE * (1 - (w + 0.5) / waves) * 0.92;
      const az = Math.random() * Math.PI * 2;
      const dist = lerp(WAVE_DIST[0], WAVE_DIST[1], Math.random());
      const count = Math.round(lerp(WAVE_MIN, WAVE_MAX, Math.random()) * this._n / 400);
      for (let k = 0; k < count; k++) {
        this._release(az, dist, 1);
        const j = (this._slot - 1 + this._n) % this._n;
        this._birth[j] = -age + Math.random() * WAVE_SPREAD_S;
        this._clockBirth[j] = this._birth[j];   // at rate 1 before the start
      }
    }
    this._upload();
  }

  _seedSingle() {
    this._ground[0] = 0;
    this._ground[1] = 1.75;
    this._birth[0] = 0;
    this._clockBirth[0] = 0;
    this._seed.set([0.55, 0.40, 0.90, 0.62]);
    this._upload();
  }

  _upload() {
    this._birthAttr.needsUpdate = true;
    this._clockBirthAttr.needsUpdate = true;
    this._fgeo.attributes.aClockBirth.needsUpdate = true;
    this._groundAttr.needsUpdate = true;
    this._seedAttr.needsUpdate = true;
    this._fgeo.attributes.aBirth.needsUpdate = true;
    this._fgeo.attributes.aGround.needsUpdate = true;
    this._fgeo.attributes.aSeed.needsUpdate = true;
    this._dirty = false;
  }

  // --------------------------------------------------------------- camera --
  _placeCamera(dt) {
    if (this._single) {
      // Close up, three quarters from slightly below - the angle the brief
      // calls the best one, and the one the camera actually gets most of the
      // time once the lanterns are overhead.
      this._camera.position.set(-0.55, 0.05, 0.0);
      this._camera.lookAt(0.05, 1.02, 1.75);
      return;
    }
    this._yaw += (dt / PAN_PERIOD) * Math.PI * 2;
    const pitch = CAM_PITCH + this._tilt;
    const c = this._camera;
    c.position.set(0, CAM_HEIGHT, 0);
    c.lookAt(
      Math.sin(this._yaw) * Math.cos(pitch) * 10,
      CAM_HEIGHT + Math.sin(pitch) * 10,
      Math.cos(this._yaw) * Math.cos(pitch) * 10
    );
  }

  // --------------------------------------------------------------- update --
  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;         // freezes the flames too, as asked
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;

    if (!this._grainSet && this._layout.grain) {
      const tex = getPaperTexture();
      if (tex) {
        this._paperUniforms.uGrain.value = tex;
        this._paperUniforms.uHasGrain.value = 1;
        this._grainSet = true;
      }
    }

    const director = profile.director || {};
    const bands = director.bands || { low: 0, mid: 0, high: 0 };
    const k = 1 - Math.exp(-dt / 0.9);   // slow: nothing here is percussive
    this._midSmooth += (clamp01(bands.mid) - this._midSmooth) * k;
    this._highSmooth += (clamp01(bands.high) - this._highSmooth) * k;
    // The wind's gain scales the drift ALREADY accumulated, so it moves on a
    // gust's time scale: at 0.9 s, lanterns high up swung sideways with the bass.
    this._windSmooth += (clamp01(bands.low) - this._windSmooth) * (1 - Math.exp(-dt / WIND_SMOOTH_S));

    // The climb clock advances at the music's rate BEFORE the releases below
    // read it, so a lantern released this frame starts at distance zero.
    // progress: the field climbs through the track. Early on a wave is still
    // near the ground; by the end the sky is mostly distant points, which is
    // what the accumulated history already produces - this only leans on it.
    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    const rate = lerp(1 - RISE_MID, 1 + RISE_MID, this._midSmooth) * lerp(0.88, 1.12, progress);
    this._clock += dt * rate;

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const vivid = profile.artworkPalette && profile.artworkPalette.vividColors;
      if (vivid && vivid.length) {
        const c = new THREE.Color(vivid[0]);
        // DESATURATE FIRST, THEN PULL TOWARD OFF-WHITE. Mixing 40% of a vivid
        // cover colour into the paper is what the reference asks for and, done
        // straight, it is far too much: vividColors are saturated by
        // construction, and 40% of a pure blue gives an unmistakably BLUE
        // lantern - the paper stops being paper. Cutting the saturation to a
        // third first keeps the cover's hue identifiable while leaving the
        // paper where rice paper belongs, which is almost white.
        c.getHSL(_hsl);
        c.setHSL(_hsl.h, Math.min(_hsl.s, PAPER_TINT_SAT), 0.72);
        this._paperUniforms.uPaper.value.setRGB(
          lerp(c.r, PAPER_BASE[0], PAPER_WHITE),
          lerp(c.g, PAPER_BASE[1], PAPER_WHITE),
          lerp(c.b, PAPER_BASE[2], PAPER_WHITE)
        );
      }
    }

    if (!this._single) {
      // --- waves, on the phrase and on nothing smaller ---
      const phrase = profile.phraseIndex;
      if (phrase != null && phrase !== this._lastPhrase) {
        if (this._lastPhrase != null) {
          this._phraseCount++;
          const big = (this._phraseCount % 4) === 0;
          const base = big ? WAVE_BIG : lerp(WAVE_MIN, WAVE_MAX, Math.random());
          this._scheduleWave(Math.max(6, Math.round(base * this._n / 400)));
        }
        this._lastPhrase = phrase;
      } else if (phrase == null && this._time > this._nextWaveAt) {
        // No grid at all - a radio stream. A release every twelve seconds or
        // so, which is what a phrase would have been.
        this._nextWaveAt = this._time + 10 + Math.random() * 5;
        this._phraseCount++;
        const big = (this._phraseCount % 4) === 0;
        const base = big ? WAVE_BIG : lerp(WAVE_MIN, WAVE_MAX, Math.random());
        this._scheduleWave(Math.max(6, Math.round(base * this._n / 400)));
      }
      if (this._forceWave) { this._forceWave = false; this._scheduleWave(Math.round(60 * this._n / 400)); }

      // --- the scheduled releases come due ---
      while (this._pendingHead < this._pending.length
             && this._pending[this._pendingHead] <= this._time) {
        this._release(this._pending[this._pendingHead + 1],
                      this._pending[this._pendingHead + 2], 1);
        this._pendingHead += 3;
      }
      if (this._pendingHead > 3000) {
        this._pending = this._pending.slice(this._pendingHead);
        this._pendingHead = 0;
      }

      // --- strays between the waves ---
      if (this._time > this._nextSingleAt) {
        this._nextSingleAt = this._time + (0.4 + Math.random()) / SINGLES_PER_S;
        this._release(this._waveAzimuth(), lerp(WAVE_DIST[0], WAVE_DIST[1], Math.random()), 0.4);
      }
      if (this._dirty) this._upload();
    }

    // --- the shared uniforms ---
    this._shared.uTime.value = this._time;
    this._shared.uWind.value = WIND_BASE * lerp(1 - WIND_LOW * 0.5, 1 + WIND_LOW * 0.5, this._windSmooth);
    this._shared.uClock.value = this._clock;
    this._shared.uHighAmp.value = this._flameFrozen ? 0
      : lerp(0.55, 1.0, this._highSmooth) * FLICKER_HIGH * 1.6;

    this._glow += (0.22 - this._glow) * Math.min(1, dt * GLOW_DECAY);
    this._skyUniforms.uGlow.value = this._glow;
    // The head lifts as a wave goes up: the kick decays, and the tilt follows
    // the kick through its own lag. Setting the tilt itself made the whole
    // frame jump 2.6 degrees on every phrase.
    this._tiltKick += (0 - this._tiltKick) * Math.min(1, dt * 0.35);
    this._tilt += (this._tiltKick - this._tilt) * Math.min(1, dt * TILT_EASE);

    this._placeCamera(dt);
    this._camera.updateMatrixWorld();
    this._skyUniforms.uInvProj.value.copy(this._camera.projectionMatrixInverse);
    this._skyUniforms.uCamWorld.value.copy(this._camera.matrixWorld);
  }

  dispose() {
    this._scene.remove(this._mesh);
    this._scene.remove(this._flames);
    this._scene.remove(this._sky);
    this._geo.dispose();
    this._cone.dispose();
    this._fgeo.dispose();
    this._paperMat.dispose();
    this._flameMat.dispose();
    this._sky.geometry.dispose();
    this._skyMat.dispose();
  }
}

const _hsl = { h: 0, s: 0, l: 0 };

const _size = new THREE.Vector2();
